/****************************************************************************
 * COPYRIGHT (c) 2025 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useEffect } from "react";
import { useSelector } from "react-redux";
import APIManager from "../../../Services/ApiManager/ApiManager";
import { Container, Switch, Typography, Chip, Stack } from "@mui/material";
import PricingTable from "../PricingTable/PricingTable";
import Grid from "@mui/material/Grid2";

function createDataPremium(
  productId,
  type,
  name,
  price,
  currencyCode,
  planId,
  lastUpdated
) {
  return {
    productId,
    type,
    name,
    price,
    currencyCode,
    planId,
    lastUpdated,
  };
}

function createData({ type, id, productId, price, trialPeriod, noOfPlans, lastModified, plans }) {
  return {
    type,
    id,
    productId,
    price,
    trialPeriod,
    noOfPlans,
    lastModified,
    plans,
  };
}

function OemAdminMonetize() {
  const [inAppRows, setInAppRows] = React.useState([]);
  const [premiumRows, setPremiumRows] = React.useState([]);
  const checkedToggle = sessionStorage.getItem("idpLogin") !== "true";

  const submissionData = useSelector(
    (state) => state.submissionDetails.submissionDetails
  );

  useEffect(() => {
    if (submissionData?.isInAppPurchase) {
      APIManager.getAppPricing(
        submissionData.wso2UserId,
        submissionData.packageId,
        "INAPP"
      ).then((json) => {
        if (json.length > 0) {
          formTableWithData(json);
        }
      });
    }
    if (submissionData?.isPremiumApp) {
      APIManager.getAppPricing(
        submissionData.wso2UserId,
        submissionData.packageId,
        "APP"
      ).then((json) => {
        if (json[0]?.product) {
          let allPlans = [];
          if (json[0].oneTimePlan !== null) {
            let oneTimePlan = {
              productId: json[0].product.id,
              name: json[0].oneTimePlan.name,
              price: json[0].oneTimePlan.basePrice.price,
              currencyCode: json[0].oneTimePlan.basePrice.currencyCode,
              planId: json[0].oneTimePlan.id,
              type: json[0].oneTimePlan.planType,
              lastUpdated: json[0].oneTimePlan.lastUpdated,
            };
            allPlans.push(oneTimePlan);
          }
          if (json[0].recurringPlans.length > 0) {
            json[0].recurringPlans.forEach((element) => {
              let recurringPlan = {
                productId: json[0].product.id,
                name: element.name,
                price: element.basePrice.price,
                currencyCode: element.basePrice.currencyCode,
                planId: element.id,
                type: element.planType,
                lastUpdated: element.lastUpdated,
              };
              allPlans.push(recurringPlan);
            });
          }
          if (allPlans.length > 0) {
            formTableWithPremiumData(allPlans);
          }
        }
      });
    }
  }, [submissionData]);

  const formTableWithData = (products) => {
    let allRows = [];
    products.forEach((product) => {
      if (product.oneTimePlan) {
        allRows.push(
          createData({
            type: "oneTime",
            id: product.product.id,
            productId: product.product.productId,
            price: product.oneTimePlan?.basePrice?.price.toFixed(2),
            trialPeriod: product.trialPrice?.trialPeriod,
            noOfPlans: 1,
            lastModified: product.product.lastUpdated,
            plans: [],
          })
        );
      } else {
        allRows.push(
          createData({
            type: "recurring",
            id: product.product.id,
            productId: product.product.productId,
            price: "-",
            trialPeriod: product.trialPrice?.trialPeriod,
            noOfPlans: product.recurringPlans.length,
            lastModified: product.product.lastUpdated,
            plans: product.recurringPlans,
          })
        );
      }
    });
    setInAppRows(allRows);
  };

  const formTableWithPremiumData = (plans) => {
    let allRows = [];
    plans.forEach((plan) => {
      allRows.push(
        createDataPremium(
          plan.productId,
          plan.type,
          plan.name,
          plan.price,
          plan.currencyCode,
          plan.planId,
          plan.lastUpdated
        )
      );
    });
    setPremiumRows(allRows);
  };

  return (
    <Container maxWidth="lg" style={{ marginBottom: "16px" }}>
      <div>
        <div>
          <section
            id="monetizePricing"
            className="rollout"
            style={
              submissionData &&
              Object.keys(submissionData).length > 0 &&
              submissionData.status === "DRAFT"
                ? { marginBottom: "20px !important", width: "85%" }
                : { marginBottom: "20px !important" }
            }
          >
            <Grid container spacing={2}>
              <Grid size={10}>
                <Typography
                  sx={{
                    "font-weight": "bold !important",
                    "font-size": "20px !important",

                    color: "rgba(18, 20, 21, 1) !important",
                    "margin-right": "auto !important",
                  }}
                  className="uploadSignedApkText"
                >
                  Monetize
                </Typography>
              </Grid>
              <Grid size={2}>
                Free
                <Switch disabled="true" checked={checkedToggle} />
                <span style={{ color: "#0476e2" }}>Paid</span>
              </Grid>
            </Grid>
            {sessionStorage.getItem("idpLogin") !==
              "true" && (
                <>
                  <Typography
                    variant="button"
                    gutterBottom
                    sx={{
                      display: "block",
                      textTransform: "none",
                      fontWeight: "bold",
                      marginTop: "2%",
                    }}
                  >
                    In-App Products Pricing
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ marginTop: "1%" }}>
                    <Chip label="FREE" sx={{ fontWeight: "bold" }} />
                    <Chip
                      label="In-App Purchases"
                      variant="outlined"
                      sx={{ border: "none", color: "#757575" }}
                    />
                  </Stack>
                  <PricingTable listType={"InApp"} data={inAppRows} />
                  <Typography
                    variant="button"
                    gutterBottom
                    sx={{
                      display: "block",
                      textTransform: "none",
                      fontWeight: "bold",
                      marginTop: "3%",
                    }}
                  >
                    App Pricing
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ marginTop: "1%" }}>
                    <Chip label="PREMIUM" sx={{ fontWeight: "bold" }} />
                    <Chip
                      label="Premium Purchases"
                      variant="outlined"
                      sx={{ border: "none", color: "#757575" }}
                    />
                  </Stack>
                  <PricingTable listType={"App"} data={premiumRows} />
                </>
              )}
          </section>
        </div>
      </div>
    </Container>
  );
}

export default OemAdminMonetize;
